/**
 * translation-service.ts
 *
 * Core translation resolver with CLDR pluralization, parameter interpolation,
 * dynamic namespace registration, language bundle hot-loading, and resilient fallback chains.
 */

import { type Bcp47LocaleTag, type TranslationParams, SUPPORTED_LOCALES } from './locale-contracts.js';
import type { LanguageBundle } from '../plugins/sdk/i18n-contracts.js';
import enCatalog from './catalogs/en.json';
import zhHansCatalog from './catalogs/zh-Hans.json';
import esCatalog from './catalogs/es.json';
import frCatalog from './catalogs/fr.json';
import deCatalog from './catalogs/de.json';
import arCatalog from './catalogs/ar.json';
import swCatalog from './catalogs/sw.json';
import lgCatalog from './catalogs/lg.json';

const BASE_CATALOGS: Record<string, Record<string, any>> = {
  en: JSON.parse(JSON.stringify(enCatalog)),
  'zh-Hans': JSON.parse(JSON.stringify(zhHansCatalog)),
  'zh-CN': JSON.parse(JSON.stringify(zhHansCatalog)),
  es: JSON.parse(JSON.stringify(esCatalog)),
  fr: JSON.parse(JSON.stringify(frCatalog)),
  de: JSON.parse(JSON.stringify(deCatalog)),
  ar: JSON.parse(JSON.stringify(arCatalog)),
  sw: JSON.parse(JSON.stringify(swCatalog)),
  lg: JSON.parse(JSON.stringify(lgCatalog)),
};


export class TranslationService {
  private catalogs: Record<string, Record<string, any>> = {};
  private dynamicNamespaces: Map<string, Map<string, Record<string, string>>> = new Map();
  private pluralRulesCache = new Map<string, Intl.PluralRules>();
  private listeners = new Set<() => void>();

  constructor() {
    this.catalogs = JSON.parse(JSON.stringify(BASE_CATALOGS));
    this.hydrateFromStorage();
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error('[TranslationService] Error in change listener:', err);
      }
    });
  }

  /**
   * Registers or overrides a plugin-scoped localization namespace.
   * e.g. registerNamespace('ar', 'droneFleet', { sidebarTitle: 'أسطول المسيرات' })
   */
  public registerNamespace(locale: string, namespace: string, catalog: Record<string, string>): void {
    const normalizedLocale = locale === 'zh-CN' ? 'zh-Hans' : locale;
    if (!this.dynamicNamespaces.has(normalizedLocale)) {
      this.dynamicNamespaces.set(normalizedLocale, new Map());
    }
    const locMap = this.dynamicNamespaces.get(normalizedLocale)!;
    const existing = locMap.get(namespace) || {};
    locMap.set(namespace, { ...existing, ...catalog });

    // Also mount into main catalog object for deep access
    if (!this.catalogs[normalizedLocale]) {
      this.catalogs[normalizedLocale] = {};
    }
    this.catalogs[normalizedLocale][namespace] = {
      ...(this.catalogs[normalizedLocale][namespace] || {}),
      ...catalog,
    };

    this.persistToStorage();
    this.notify();
  }

  /**
   * Hot-loads a complete LanguageBundle into the translation service.
   */
  public registerBundle(bundle: LanguageBundle): void {
    const rawLocale = bundle.metadata?.locale || 'en';
    const locale = rawLocale === 'zh-CN' ? 'zh-Hans' : rawLocale;

    if (!this.catalogs[locale]) {
      this.catalogs[locale] = JSON.parse(JSON.stringify(this.catalogs.en || {}));
    }

    // Merge menus
    if (bundle.menuTranslations) {
      if (!this.catalogs[locale].menus) this.catalogs[locale].menus = {};
      Object.assign(this.catalogs[locale].menus, bundle.menuTranslations);
    }

    // Merge channels
    if (bundle.channelTranslations) {
      if (!this.catalogs[locale].channels) this.catalogs[locale].channels = {};
      Object.assign(this.catalogs[locale].channels, bundle.channelTranslations);
    }

    // Merge common
    if (bundle.commonTranslations) {
      if (!this.catalogs[locale].common) this.catalogs[locale].common = {};
      Object.assign(this.catalogs[locale].common, bundle.commonTranslations);
    }

    // Merge plugin namespaces
    if (bundle.pluginNamespaces) {
      for (const [namespace, catalog] of Object.entries(bundle.pluginNamespaces)) {
        this.registerNamespace(locale, namespace, catalog);
      }
    }

    this.persistToStorage();
    this.notify();
  }

  /**
   * Exports an active locale as a downloadable LanguageBundle.
   */
  public exportLanguageBundle(locale: string): LanguageBundle {
    const normalized = locale === 'zh-CN' ? 'zh-Hans' : locale;
    const cat = this.catalogs[normalized] || this.catalogs.en || {};
    const locDef = (SUPPORTED_LOCALES as any)[normalized] || {
      locale: normalized,
      languageName: normalized.toUpperCase(),
      nativeName: normalized.toUpperCase(),
      direction: 'ltr',
    };

    const pluginNamespaces: Record<string, Record<string, string>> = {};
    const locMap = this.dynamicNamespaces.get(normalized);
    if (locMap) {
      for (const [ns, dict] of locMap.entries()) {
        pluginNamespaces[ns] = { ...dict };
      }
    }

    return {
      metadata: {
        bundleId: `bundle-${normalized}-${Date.now()}`,
        locale: normalized,
        languageName: locDef.languageName || normalized,
        nativeName: locDef.nativeName || normalized,
        direction: locDef.direction || 'ltr',
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
      },
      menuTranslations: { ...(cat.menus || {}) },
      channelTranslations: { ...(cat.channels || {}) },
      commonTranslations: { ...(cat.common || {}) },
      pluginNamespaces,
    };
  }

  /**
   * Retrieves list of registered namespaces for a locale.
   */
  public getRegisteredNamespaces(locale: string): string[] {
    const normalized = locale === 'zh-CN' ? 'zh-Hans' : locale;
    const locMap = this.dynamicNamespaces.get(normalized);
    return locMap ? Array.from(locMap.keys()) : [];
  }

  private getPluralRule(locale: Bcp47LocaleTag, count: number): Intl.LDMLPluralRule {
    const dateTimeLocale = (SUPPORTED_LOCALES as any)[locale]?.dateTimeLocale || 'en-US';
    if (!this.pluralRulesCache.has(dateTimeLocale)) {
      this.pluralRulesCache.set(dateTimeLocale, new Intl.PluralRules(dateTimeLocale));
    }
    return this.pluralRulesCache.get(dateTimeLocale)!.select(count);
  }

  private resolveKey(catalog: Record<string, any>, key: string): any {
    if (!catalog || typeof catalog !== 'object') return undefined;

    // Direct key match
    if (catalog[key] !== undefined) {
      return catalog[key];
    }

    // Colon namespace syntax: "droneFleet:sidebarTitle"
    if (key.includes(':')) {
      const [ns, subKey] = key.split(':', 2);
      if (ns && subKey) {
        const nsCatalog = catalog[ns];
        if (nsCatalog && typeof nsCatalog === 'object') {
          const res = this.resolveKey(nsCatalog, subKey);
          if (res !== undefined) return res;
        }
      }
    }

    // Dot notation syntax: "channels.gallery.title"
    const parts = key.split('.');
    let current = catalog;
    for (const part of parts) {
      if (current === undefined || current === null || typeof current !== 'object') {
        return undefined;
      }
      current = current[part];
    }
    return current;
  }

  public translate(key: string, params?: TranslationParams, currentLocale: string = 'en'): string {
    const normalizedLocale = currentLocale === 'zh-CN' ? 'zh-Hans' : currentLocale;
    const targetCatalog = this.catalogs[normalizedLocale] || this.catalogs.en || {};
    let raw = this.resolveKey(targetCatalog, key);

    // Fallback 1: Default fallback locale (en)
    if (raw === undefined && normalizedLocale !== 'en') {
      const enCatalogObj = this.catalogs.en || {};
      raw = this.resolveKey(enCatalogObj, key);
    }

    // Fallback 2: Stable key identifier
    if (raw === undefined) {
      return key;
    }

    // Handle Pluralization Objects
    if (typeof raw === 'object' && raw !== null && params && typeof params.count === 'number') {
      const count = params.count;
      const rule = this.getPluralRule(normalizedLocale as Bcp47LocaleTag, count);
      raw = raw[rule] || raw.other || raw.one || Object.values(raw)[0] || key;
    }

    if (typeof raw !== 'string') {
      return typeof raw === 'object' ? key : String(raw);
    }

    // Parameter Interpolation {key}
    if (params) {
      return raw.replace(/\{([a-zA-Z0-9_-]+)\}/g, (match, paramName) => {
        const val = params[paramName];
        return val !== undefined ? String(val) : match;
      });
    }

    return raw;
  }

  private persistToStorage(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const serializableNamespaces: Record<string, Record<string, Record<string, string>>> = {};
      for (const [locale, nsMap] of this.dynamicNamespaces.entries()) {
        serializableNamespaces[locale] = {};
        for (const [ns, dict] of nsMap.entries()) {
          serializableNamespaces[locale][ns] = dict;
        }
      }
      localStorage.setItem('mesh_i18n_dynamic_namespaces', JSON.stringify(serializableNamespaces));
    } catch {
      // Storage unavailable or disabled
    }
  }

  private hydrateFromStorage(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem('mesh_i18n_dynamic_namespaces');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          for (const [locale, nsMap] of Object.entries(parsed)) {
            if (nsMap && typeof nsMap === 'object') {
              for (const [ns, dict] of Object.entries(nsMap as any)) {
                if (dict && typeof dict === 'object') {
                  this.registerNamespace(locale, ns, dict as Record<string, string>);
                }
              }
            }
          }
        }
      }
    } catch {
      // Ignore hydration errors
    }
  }
}

export const globalTranslationService = new TranslationService();
